import type { SpecialType } from '@iheartjane/types';
import compact from 'lodash-es/compact';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import {
  useBrandsForRulesets,
  useCategoriesForRulesets,
  useProductsById,
} from '@jane/business-admin/data-access';
import type { RulesEntities } from '@jane/business-admin/types';
import type {
  ConditionApplyToOption,
  ConditionSection,
  ConditionSet,
} from '@jane/shared-b2b/components';
import { getSelectedValues } from '@jane/shared-b2b/components';
import { lineageOptions, weightValueOptions } from '@jane/shared-b2b/util';

import {
  BRANDS_KEY,
  toBrandNames,
  toBrandsSection,
} from './util/toBrandsSection';
import { toCategorySection } from './util/toCategorySection';
import { PRODUCTS_KEY, toProductsSection } from './util/toProductsSection';

export interface UseConditionSectionsArgs {
  /** The editor's groups, read for the ids whose rows the sections need fetched */
  conditionSets: ConditionSet[];
  isGlobal: boolean;
  orgId?: number;
  /** Names for the brands the rules reference, keyed by product brand id */
  knownBrands?: RulesEntities['brands'];
  /** The products section's modal. Memoize it: every section rebuilds when it changes */
  renderProductsModal: () => ReactNode;
  /** Bulk pricing pairs weight with price; every other type keeps them apart */
  specialType?: SpecialType | '';
  storeId: string;
}

/**
 * Orchestrates fetching and assembling the data needed to populate the condition
 * sections, per "Apply to" choice a condition group can be set to.
 */
export const useConditionSections = ({
  conditionSets,
  isGlobal,
  knownBrands,
  orgId,
  renderProductsModal,
  specialType,
  storeId,
}: UseConditionSectionsArgs): ConditionApplyToOption[] => {
  const { data: categories } = useCategoriesForRulesets({
    isGlobal,
    orgId,
    storeId,
  });

  // The catalog searches server-side, so the query is a fetch parameter rather
  // than a filter over what was already fetched
  const [brandQuery, setBrandQuery] = useState('');

  const { data: brandCatalog, fetchNextPage: fetchMoreBrands } =
    useBrandsForRulesets({ isGlobal, names: brandQuery, orgId, storeId });

  /** Names every brand a page has surfaced, keyed by product brand id */
  const [seenBrands, setSeenBrands] = useState<
    NonNullable<RulesEntities['brands']>
  >({});
  const [syncedCatalog, setSyncedCatalog] = useState<typeof brandCatalog>();

  // A page names brands the special itself does not, and the catalog drops
  // them the moment it answers another query — so their names are kept as
  // each page lands rather than read back off one
  if (brandCatalog !== syncedCatalog) {
    setSyncedCatalog(brandCatalog);
    setSeenBrands((current) => ({ ...current, ...toBrandNames(brandCatalog) }));
  }

  /** What the special carries wins: those names came with the rules */
  const namedBrands = useMemo(
    () => ({ ...seenBrands, ...knownBrands }),
    [knownBrands, seenBrands]
  );

  /** Every group's selected brands, included and excluded */
  const selectedBrandIds = useMemo(
    () =>
      [
        ...new Set([
          ...getSelectedValues(conditionSets, 'excludes', BRANDS_KEY),
          ...getSelectedValues(conditionSets, 'includes', BRANDS_KEY),
        ]),
      ].map(Number),
    [conditionSets]
  );

  // Memoized so the sections below are not rebuilt on every render
  const onBrandsEndReached = useCallback(() => {
    fetchMoreBrands();
  }, [fetchMoreBrands]);

  /** Every group's selected products, included and excluded, in one fetch */
  const productIds = useMemo(
    () => [
      ...new Set([
        ...getSelectedValues(conditionSets, 'excludes', PRODUCTS_KEY),
        ...getSelectedValues(conditionSets, 'includes', PRODUCTS_KEY),
      ]),
    ],
    [conditionSets]
  );

  // Paginated sections list only the selection, so their tags need the rows
  const { products: selectedProducts } = useProductsById({
    ids: productIds,
    isGlobal,
    orgId,
    storeId,
  });
  return useMemo(() => {
    const weightAndPriceConditions: ConditionSection[] =
      specialType === 'bulk_pricing'
        ? [
            {
              allowedDecimalPlaces: 2,
              items: weightValueOptions(),
              key: 'weight_and_price',
              label: 'Weight and price',
              range: {
                label: 'Price',
                min: 0,
              },
              startUnit: '$',
              step: 0.01,
              type: 'multiselect-range',
            },
          ]
        : [
            {
              allowExcludes: true,
              items: weightValueOptions(),
              key: 'weight_values',
              label: 'Weights',
              type: 'multiselect',
            },
            {
              allowedDecimalPlaces: 2,
              items: [
                {
                  label: 'Price',
                  min: 0,
                },
              ],
              key: 'price',
              label: 'Price',
              startUnit: '$',
              step: 0.01,
              type: 'range',
            },
          ];

    return [
      {
        conditions: compact([
          toCategorySection(categories),
          toBrandsSection({
            catalog: brandCatalog ?? [],
            namedBrands,
            onEndReached: onBrandsEndReached,
            onSearch: setBrandQuery,
            selectedIds: selectedBrandIds,
          }),
          {
            allowExcludes: true,
            items: lineageOptions(),
            key: 'lineages',
            label: 'Lineage',
            modalVariant: 'standard-dialogue',
            type: 'multiselect',
          } satisfies ConditionSection,
          ...weightAndPriceConditions,
          toProductsSection(
            selectedProducts,
            renderProductsModal,
            'attributes'
          ),
        ]),
        label: 'Category, brand, and more',
        value: 'attributes',
      },
      {
        conditions: [
          toProductsSection(selectedProducts, renderProductsModal, 'products'),
          ...weightAndPriceConditions,
        ],
        label: 'Individual products',
        value: 'products',
      },
    ];
  }, [
    brandCatalog,
    categories,
    namedBrands,
    onBrandsEndReached,
    renderProductsModal,
    selectedBrandIds,
    selectedProducts,
    specialType,
  ]);
};
